// src/hr/kit/Toast.js -- a small confirmation that something was saved.
// The live region is always in the page so screen readers announce each new message; the toast
// itself slides up, stays a few seconds and can be dismissed early.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';

const SHOW_MS = 4500;

// toast: { id, text } or null. onDismiss is called when it times out or is closed.
export function Toast({ toast, onDismiss, duration = SHOW_MS }) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const id = toast ? toast.id : null;

  useEffect(() => {
    if (id == null) return undefined;
    const timer = setTimeout(() => dismiss.current && dismiss.current(), duration);
    return () => clearTimeout(timer);
  }, [id, duration]);

  return (
    <div className="hrp-toasts" role="status" aria-live="polite" aria-atomic="true">
      {toast && (
        <div className="hrp-toast" key={toast.id}>
          <span className="hrp-toast__ic" aria-hidden="true"><FiCheck /></span>
          <span className="hrp-toast__text">{toast.text}</span>
          <button type="button" className="hrp-iconbtn hrp-toast__x" aria-label="Dismiss" onClick={() => dismiss.current && dismiss.current()}>
            <FiX aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

// [toast, show(text), dismiss] -- each show() replaces the current message and restarts its timer.
export function useToast() {
  const [toast, setToast] = useState(null);
  const counter = useRef(0);
  const show = useCallback((text) => {
    counter.current += 1;
    setToast({ id: counter.current, text });
  }, []);
  const dismiss = useCallback(() => setToast(null), []);
  return [toast, show, dismiss];
}

export default Toast;
